import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Sparkles, Code2, Palette, TrendingUp, Smartphone, ShoppingBag, Cloud, CircleDollarSign, HeartPulse, GraduationCap, Building2, Plane, Newspaper, Check } from 'lucide-react';
// import { Star, Quote } from 'lucide-react'; // used by the commented-out Testimonials section below
import { FadeUp, TextReveal, BlurReveal, Counter } from '@/components/site/Reveal';
import MagneticButton from '@/components/site/MagneticButton';
import { fetchServices, fetchProjects, fetchPricing, fetchFaqs, fetchPosts, STATIC, submitLead } from '@/lib/cms';
// import { fetchTestimonials } from '@/lib/cms'; // disabled until real testimonials exist
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import SEO from '@/components/site/SEO';

const ICONS = { Sparkles, Code2, Palette, TrendingUp, Smartphone, ShoppingBag, Cloud, CircleDollarSign, HeartPulse, GraduationCap, Building2, Plane, Newspaper };

// Pricing hidden for now — set to true to restore the pricing section.
const SHOW_PRICING = false;

export default function Home() {
  const [services, setServices] = useState(STATIC.services);
  const [projects, setProjects] = useState(STATIC.projects);
  // const [testimonials, setTestimonials] = useState(STATIC.testimonials); // disabled until real testimonials exist
  const [pricing, setPricing] = useState(STATIC.pricing);
  const [faqs, setFaqs] = useState(STATIC.faqs);
  const [posts, setPosts] = useState(STATIC.posts);

  useEffect(() => {
    fetchServices().then(setServices);
    fetchProjects().then(setProjects);
    // fetchTestimonials().then(setTestimonials); // disabled until real testimonials exist
    if (SHOW_PRICING) fetchPricing().then(setPricing);
    fetchFaqs().then(setFaqs);
    fetchPosts().then(setPosts);
  }, []);

  return (
    <>
      <SEO
        title="Slinkflow — Premium web design & development agency"
        description="We design and engineer premium digital experiences that drive revenue for ambitious brands."
      />
      <Hero />
      <Marquee />
      <Services services={services} />
      <Stats />
      <Industries />
      <WhyUs />
      <FeaturedProjects projects={projects.filter(p => p.featured).slice(0, 4)} />
      <Process />
      {/* <Testimonials items={testimonials} /> disabled until real testimonials exist — see commented component below */}
      {SHOW_PRICING && <PricingPreview items={pricing} />}
      <BlogPreview posts={posts.slice(0, 3)} />
      <FAQSection items={faqs} />
      <CTABand />
    </>
  );
}

/* ===================== TESTIMONIALS (disabled until real testimonials exist) =====================
   To re-enable: uncomment the two `fetchTestimonials`/`Star, Quote` imports above, the
   `testimonials` state + fetch call in Home(), the <Testimonials .../> render line, and this
   whole block below.
function Testimonials({ items }) {
  return (
    <section className="bg-ink-50 py-32 lg:py-40" data-testid="home-testimonials">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid items-end gap-10 lg:grid-cols-12">
          <FadeUp className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.3em] text-cyan">Clients</p>
            <h2 className="mt-4 font-display text-[44px] font-light leading-[1.05] tracking-tight md:text-[60px]">
              Trusted by <span className="gradient-text font-medium">people who care.</span>
            </h2>
          </FadeUp>
          <Link to="/testimonials" className="lg:col-span-5 lg:text-right text-[15px] font-medium text-ink-900 link-underline">Read more stories →</Link>
        </div>

        <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {items.filter(t => t.featured).slice(0, 3).map((t, i) => (
            <FadeUp key={t.id} delay={i * 0.06}>
              <div className="relative h-full rounded-3xl bg-white p-8 shadow-soft">
                <Quote className="absolute right-6 top-6 h-8 w-8 text-cyan/20" />
                <div className="flex items-center gap-1 text-cyan">
                  {Array.from({ length: t.rating }).map((_, j) => <Star key={j} className="h-4 w-4 fill-cyan" />)}
                </div>
                <p className="mt-6 text-[18px] leading-relaxed text-ink-900">&ldquo;{t.quote}&rdquo;</p>
                <div className="mt-8 flex items-center gap-3 border-t border-ink-100 pt-6">
                  <img src={t.avatar} alt={t.name} loading="lazy" className="h-10 w-10 rounded-full object-cover" />
                  <div>
                    <p className="font-medium text-ink-900">{t.name}</p>
                    <p className="text-sm text-ink-500">{t.role}</p>
                  </div>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}
*/

/* ===================== HERO ===================== */
function Hero() {
  return (
    <section className="relative overflow-hidden pt-40 pb-32 md:pt-48 md:pb-40 lg:pt-56 lg:pb-48">
      <div className="absolute -top-32 -left-20 h-[620px] w-[620px] hero-gradient-orb animate-blob" />
      <div className="absolute right-0 top-1/3 h-[480px] w-[480px] hero-orb-2 animate-blob" style={{ animationDelay: '4s' }} />
      <div className="absolute inset-0 grain pointer-events-none" />

      <div className="relative mx-auto max-w-[1400px] px-6 md:px-10">
        <FadeUp className="mb-8 inline-flex items-center gap-2 rounded-full border border-ink-100 bg-white/70 backdrop-blur px-4 py-2 text-xs uppercase tracking-[0.2em] text-ink-500" data-testid="hero-badge">
          <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cyan opacity-75" /><span className="relative inline-flex h-2 w-2 rounded-full bg-cyan" /></span>
          Now booking Q2 2026
        </FadeUp>

        <h1 className="max-w-[1100px] font-display text-[56px] font-light leading-[1.02] tracking-[-0.03em] text-ink-900 md:text-[88px] lg:text-[120px]">
          We design
          <br />
          <span className="font-medium gradient-text">digital perfection.</span>
        </h1>

        <FadeUp delay={0.5} className="mt-10 grid items-end gap-10 lg:grid-cols-12">
          <p className="lg:col-span-6 max-w-[640px] text-balance text-[18px] leading-relaxed text-ink-500 md:text-[20px]">
            Slinkflow is a premium design & engineering studio. We build websites, products and brands that compound — turning visitors into paying customers, and customers into evangelists.
          </p>

          <div className="lg:col-span-6 flex flex-wrap items-center gap-4 lg:justify-end">
            <Link to="/contact" data-cursor>
              <MagneticButton as="span" data-testid="hero-cta-primary" className="btn-cyan group rounded-full px-7 py-5 text-[15px] font-medium">
                Start a project
                <ArrowUpRight className="ml-1.5 h-4 w-4 transition-transform group-hover:rotate-45" />
              </MagneticButton>
            </Link>
            <Link to="/portfolio" data-cursor data-testid="hero-cta-secondary" className="group inline-flex items-center gap-2 rounded-full border border-ink-200 bg-white/70 backdrop-blur px-7 py-5 text-[15px] font-medium text-ink-900 transition hover:border-cyan hover:text-cyan">
              See our work
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
            </Link>
          </div>
        </FadeUp>

        <BlurReveal delay={0.7} className="mt-20 grid grid-cols-2 gap-8 border-t border-ink-100 pt-10 md:grid-cols-4">
          {STATIC.stats.map((s, i) => (
            <div key={i}>
              <p className="font-display text-4xl font-light text-ink-900 md:text-5xl">
                <Counter to={s.value} suffix={s.suffix} />
              </p>
              <p className="mt-2 text-sm text-ink-500">{s.label}</p>
            </div>
          ))}
        </BlurReveal>
      </div>
    </section>
  );
}

/* ===================== MARQUEE ===================== */
function Marquee() {
  const items = [...STATIC.clients, ...STATIC.clients];
  return (
    <section className="border-y border-ink-100 bg-white py-10 overflow-hidden" data-testid="client-marquee">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 mb-6">
        <p className="text-xs uppercase tracking-[0.3em] text-ink-400">Trusted by category-defining teams</p>
      </div>
      <div className="relative">
        <div className="flex gap-16 animate-marquee whitespace-nowrap">
          {items.map((c, i) => (
            <span key={i} className="font-display text-3xl font-light text-ink-300 transition-colors hover:text-cyan md:text-4xl">{c}</span>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ===================== SERVICES ===================== */
function Services({ services }) {
  return (
    <section className="relative py-32 lg:py-40" data-testid="home-services">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid items-end gap-10 lg:grid-cols-12">
          <FadeUp className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.3em] text-cyan">Services</p>
            <h2 className="mt-4 font-display text-[44px] font-light leading-[1.05] tracking-tight md:text-[64px]">
              Premium craft, <span className="gradient-text font-medium">end to end.</span>
            </h2>
          </FadeUp>
          <FadeUp delay={0.1} className="lg:col-span-5">
            <p className="text-[18px] leading-relaxed text-ink-500">
              From research and brand strategy to engineering and growth — we partner with founders and product leaders to ship work that moves the metrics that matter.
            </p>
          </FadeUp>
        </div>

        <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {services.slice(0, 6).map((s, i) => {
            const Icon = ICONS[s.icon] || Sparkles;
            return (
              <FadeUp key={s.id} delay={i * 0.06}>
                <Link to={`/services/${s.slug}`} data-cursor data-cursor-label="View" data-testid={`service-card-${s.slug}`} className="group relative block h-full overflow-hidden rounded-3xl border border-ink-100 bg-white p-8 transition-all duration-500 hover:-translate-y-1 hover:border-cyan/40 hover:shadow-hover">
                  <div className="absolute -right-12 -top-12 h-40 w-40 rounded-full bg-cyan-soft opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                  <div className="relative">
                    <div className="mb-8 flex items-center justify-between">
                      <span className="font-display text-xs text-ink-300">0{s.order_index}</span>
                      <div className="grid h-12 w-12 place-items-center rounded-2xl bg-ink-50 text-ink-900 transition-colors group-hover:bg-cyan group-hover:text-white">
                        <Icon className="h-5 w-5" strokeWidth={1.5} />
                      </div>
                    </div>
                    <h3 className="font-display text-[26px] font-medium text-ink-900">{s.title}</h3>
                    <p className="mt-1 text-[14px] text-cyan">{s.tagline}</p>
                    <p className="mt-5 text-[15px] leading-relaxed text-ink-500">{s.short}</p>
                    <div className="mt-8 flex items-center gap-2 text-sm font-medium text-ink-900">
                      Explore service
                      <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                    </div>
                  </div>
                </Link>
              </FadeUp>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ===================== STATS BAND ===================== */
function Stats() {
  return (
    <section className="border-y border-ink-100 bg-ink-50 py-16 md:py-20">
      <div className="mx-auto grid max-w-[1400px] gap-10 px-6 md:grid-cols-3 md:px-10">
        {[
          { k: '0.8s', v: 'Average load time across our shipped sites' },
          { k: '+143%', v: 'Average lift in qualified leads in the first 90 days' },
          { k: '$2.4B', v: 'In revenue influenced by Slinkflow-built experiences' },
        ].map((s, i) => (
          <FadeUp key={i} delay={i * 0.08}>
            <p className="font-display text-5xl font-light text-ink-900 md:text-6xl">{s.k}</p>
            <p className="mt-3 max-w-xs text-[15px] text-ink-500">{s.v}</p>
          </FadeUp>
        ))}
      </div>
    </section>
  );
}

/* ===================== INDUSTRIES ===================== */
function Industries() {
  return (
    <section className="py-32 lg:py-40" data-testid="home-industries">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="max-w-3xl">
          <p className="text-xs uppercase tracking-[0.3em] text-cyan">Industries</p>
          <h2 className="mt-4 font-display text-[44px] font-light leading-[1.05] tracking-tight md:text-[60px]">
            Specialists across <span className="gradient-text font-medium">eight verticals.</span>
          </h2>
        </div>

        <div className="mt-16 grid grid-cols-2 gap-px overflow-hidden rounded-3xl border border-ink-100 bg-ink-100 md:grid-cols-4">
          {STATIC.industries.map((ind, i) => {
            const Icon = ICONS[ind.icon] || Sparkles;
            return (
              <FadeUp key={ind.name} delay={i * 0.04} className="group bg-white p-10 transition-colors hover:bg-ink-50">
                <Icon className="h-7 w-7 text-ink-300 transition-colors group-hover:text-cyan" strokeWidth={1.4} />
                <p className="mt-8 font-display text-2xl text-ink-900">{ind.name}</p>
              </FadeUp>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ===================== WHY US ===================== */
function WhyUs() {
  const items = [
    { t: 'Outcome-led, always', d: 'Every decision ties back to a business metric — revenue, retention, qualified leads. We measure what matters.' },
    { t: 'Senior teams only', d: 'No juniors learning on your project. Every Slinkflow contributor has 8+ years shipping premium work.' },
    { t: 'Engineering bar of a product studio', d: 'Type-safe, tested, accessible, performant. We treat your code like our own product.' },
    { t: 'Honest, fixed scope', d: 'Detailed quotes. No scope creep. No surprise invoices. Ever.' },
  ];
  return (
    <section className="bg-ink-50 py-32 lg:py-40">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid gap-16 lg:grid-cols-12">
          <FadeUp className="lg:col-span-5 lg:sticky lg:top-32 self-start">
            <p className="text-xs uppercase tracking-[0.3em] text-cyan">Why Slinkflow</p>
            <h2 className="mt-4 font-display text-[44px] font-light leading-[1.05] tracking-tight md:text-[60px]">
              Built to be your <span className="gradient-text font-medium">last agency.</span>
            </h2>
            <p className="mt-6 text-[18px] leading-relaxed text-ink-500">
              We&apos;re intentionally small, intentionally selective and intentionally senior. We pick a handful of partners each quarter — and ship work we&apos;re proud to put our name on.
            </p>
          </FadeUp>
          <div className="lg:col-span-7 space-y-4">
            {items.map((it, i) => (
              <FadeUp key={it.t} delay={i * 0.06}>
                <div className="group rounded-3xl border border-white/0 bg-white p-8 transition-all hover:border-cyan/30 hover:shadow-hover">
                  <div className="flex items-start gap-5">
                    <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-cyan-soft text-cyan">
                      <Check className="h-5 w-5" strokeWidth={2} />
                    </div>
                    <div>
                      <h3 className="font-display text-2xl text-ink-900">{it.t}</h3>
                      <p className="mt-2 text-[15px] leading-relaxed text-ink-500">{it.d}</p>
                    </div>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ===================== FEATURED PROJECTS ===================== */
function FeaturedProjects({ projects }) {
  return (
    <section className="py-32 lg:py-40" data-testid="home-projects">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="mb-16 flex flex-wrap items-end justify-between gap-6">
          <FadeUp>
            <p className="text-xs uppercase tracking-[0.3em] text-cyan">Selected work</p>
            <h2 className="mt-4 font-display text-[44px] font-light leading-[1.05] tracking-tight md:text-[60px]">
              Recent <span className="gradient-text font-medium">case studies.</span>
            </h2>
          </FadeUp>
          <Link to="/portfolio" data-cursor className="group inline-flex items-center gap-2 text-[15px] font-medium text-ink-900">
            View all projects
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
          </Link>
        </div>

        <div className="grid gap-8 md:grid-cols-2">
          {projects.map((p, i) => (
            <FadeUp key={p.id} delay={i * 0.06}>
              <Link to={`/portfolio/${p.slug}`} data-cursor data-cursor-label="View case" data-testid={`project-card-${p.slug}`} className="group block">
                <div className="relative overflow-hidden rounded-3xl bg-ink-50">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={p.cover} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-[1.06]" />
                  </div>
                  <div className="absolute left-4 top-4 inline-flex items-center gap-2 rounded-full bg-white/85 backdrop-blur px-3 py-1.5 text-[11px] uppercase tracking-widest text-ink-700">{p.category}</div>
                </div>
                <div className="mt-6 flex items-start justify-between gap-6">
                  <div>
                    <h3 className="font-display text-[28px] text-ink-900">{p.title}</h3>
                    <p className="mt-1 text-[15px] text-ink-500">{p.tagline}</p>
                  </div>
                  <div className="hidden flex-wrap gap-2 md:flex">
                    {p.metrics?.slice(0, 2).map((m, j) => (
                      <span key={j} className="rounded-full bg-ink-50 px-3 py-1.5 text-xs font-medium text-ink-700">{m.value} {m.label}</span>
                    ))}
                  </div>
                </div>
              </Link>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ===================== PROCESS TIMELINE ===================== */
function Process() {
  return (
    <section className="relative bg-white py-32 lg:py-40">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="max-w-3xl">
          <p className="text-xs uppercase tracking-[0.3em] text-cyan">How we work</p>
          <h2 className="mt-4 font-display text-[44px] font-light leading-[1.05] tracking-tight md:text-[60px]">
            A repeatable path to <span className="gradient-text font-medium">excellent outcomes.</span>
          </h2>
        </div>

        <div className="relative mt-20">
          <div className="absolute left-[18px] top-2 bottom-2 hidden w-px bg-gradient-to-b from-cyan via-ink-100 to-transparent md:block" />
          <div className="space-y-12">
            {STATIC.process.map((p, i) => (
              <FadeUp key={p.step} delay={i * 0.05}>
                <div className="grid gap-6 md:grid-cols-12">
                  <div className="md:col-span-4 flex items-center gap-5">
                    <div className="grid h-10 w-10 place-items-center rounded-full border border-cyan bg-white font-display text-sm text-cyan">{p.step}</div>
                    <p className="font-display text-3xl text-ink-900">{p.title}</p>
                  </div>
                  <p className="md:col-span-8 text-[16px] leading-relaxed text-ink-500 md:text-[18px]">{p.body}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ===================== PRICING PREVIEW ===================== */
function PricingPreview({ items }) {
  return (
    <section className="py-32 lg:py-40">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="mb-16 max-w-3xl">
          <p className="text-xs uppercase tracking-[0.3em] text-cyan">Pricing</p>
          <h2 className="mt-4 font-display text-[44px] font-light leading-[1.05] tracking-tight md:text-[60px]">
            Premium work, <span className="gradient-text font-medium">honest pricing.</span>
          </h2>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {items.map((p, i) => (
            <FadeUp key={p.id} delay={i * 0.05}>
              <div className={`group relative flex h-full flex-col overflow-hidden rounded-3xl border bg-white p-8 transition-all hover:-translate-y-1 hover:shadow-hover ${p.featured ? 'border-cyan/40 ring-1 ring-cyan/20' : 'border-ink-100'}`}>
                {p.featured && <span className="absolute right-5 top-5 rounded-full bg-cyan-soft px-3 py-1 text-[10px] font-medium uppercase tracking-widest text-cyan-700">Most popular</span>}
                <p className="font-display text-2xl text-ink-900">{p.name}</p>
                <p className="mt-1 text-sm text-ink-500">{p.tagline}</p>
                <div className="mt-6 flex items-end gap-2">
                  {p.price != null ? (
                    <><span className="font-display text-5xl font-light text-ink-900">${(p.price/1000).toFixed(p.price%1000===0?0:1)}k</span><span className="text-sm text-ink-500">/{p.billing}</span></>
                  ) : (
                    <span className="font-display text-3xl font-light text-ink-900">Custom</span>
                  )}
                </div>
                <ul className="mt-8 space-y-3">
                  {p.features.slice(0, 5).map((f, j) => (
                    <li key={j} className="flex items-start gap-2 text-[14px] text-ink-700">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-cyan" /> {f}
                    </li>
                  ))}
                </ul>
                <Link to="/contact" data-testid={`pricing-cta-${p.name.toLowerCase()}`} className={`mt-8 inline-flex items-center justify-center gap-2 rounded-full py-3.5 text-sm font-medium transition ${p.featured ? 'btn-cyan' : 'border border-ink-200 text-ink-900 hover:border-cyan hover:text-cyan'}`}>
                  {p.cta} <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ===================== BLOG PREVIEW ===================== */
function BlogPreview({ posts }) {
  return (
    <section className="bg-ink-50 py-32 lg:py-40">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="mb-16 flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-cyan">Journal</p>
            <h2 className="mt-4 font-display text-[44px] font-light leading-[1.05] tracking-tight md:text-[60px]">
              Field notes from the <span className="gradient-text font-medium">studio.</span>
            </h2>
          </div>
          <Link to="/blog" data-cursor className="group inline-flex items-center gap-2 text-[15px] font-medium text-ink-900">
            All posts <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
          </Link>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {posts.map((b, i) => (
            <FadeUp key={b.id} delay={i * 0.06}>
              <Link to={`/blog/${b.slug}`} data-cursor data-cursor-label="Read" className="group block overflow-hidden rounded-3xl bg-white shadow-soft transition-all hover:-translate-y-1 hover:shadow-hover">
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={b.cover} alt={b.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1.4s] group-hover:scale-105" />
                </div>
                <div className="p-7">
                  <div className="flex items-center gap-3 text-xs text-ink-400">
                    <span className="rounded-full bg-cyan-soft px-2.5 py-1 text-[11px] font-medium uppercase tracking-widest text-cyan-700">{b.category}</span>
                    <span>{b.read_minutes} min read</span>
                  </div>
                  <h3 className="mt-4 font-display text-[22px] leading-snug text-ink-900 group-hover:text-cyan">{b.title}</h3>
                  <p className="mt-3 text-[14px] leading-relaxed text-ink-500">{b.excerpt}</p>
                </div>
              </Link>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ===================== FAQ ===================== */
function FAQSection({ items }) {
  return (
    <section className="py-32 lg:py-40">
      <div className="mx-auto grid max-w-[1400px] gap-16 px-6 md:px-10 lg:grid-cols-12">
        <FadeUp className="lg:col-span-5">
          <p className="text-xs uppercase tracking-[0.3em] text-cyan">FAQ</p>
          <h2 className="mt-4 font-display text-[44px] font-light leading-[1.05] tracking-tight md:text-[56px]">
            Things you might <span className="gradient-text font-medium">be wondering.</span>
          </h2>
          <p className="mt-6 text-[16px] text-ink-500">Can&apos;t find the answer? Email us at <a className="text-cyan link-underline" href="mailto:hello@slinkflow.com">hello@slinkflow.com</a></p>
        </FadeUp>
        <div className="lg:col-span-7">
          <Accordion type="single" collapsible className="w-full divide-y divide-ink-100 border-y border-ink-100">
            {items.map((f, i) => (
              <AccordionItem key={f.id} value={`item-${i}`} className="border-0">
                <AccordionTrigger data-testid={`faq-${i}`} className="py-6 text-left text-[18px] font-medium hover:text-cyan hover:no-underline md:text-[20px]">{f.question}</AccordionTrigger>
                <AccordionContent className="pb-6 text-[15px] leading-relaxed text-ink-500">{f.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

/* ===================== CTA BAND ===================== */
function CTABand() {
  return (
    <section className="relative overflow-hidden bg-ink-900 py-28 text-white md:py-36">
      <div className="absolute -top-32 left-1/4 h-[520px] w-[520px] hero-gradient-orb opacity-60" />
      <div className="absolute right-0 bottom-0 h-[420px] w-[420px] hero-orb-2 opacity-50" />
      <div className="relative mx-auto max-w-[1400px] px-6 md:px-10 text-center">
        <FadeUp>
          <p className="text-xs uppercase tracking-[0.3em] text-cyan-400">Ready when you are</p>
          <h2 className="mx-auto mt-6 max-w-4xl font-display text-[44px] font-light leading-[1.05] md:text-[72px] lg:text-[88px]">
            Let&apos;s build something <span className="gradient-text font-medium">worth talking about.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-[18px] text-white/70">A 30-minute call. Three specific wins. Zero obligation.</p>
          <div className="mt-10 flex justify-center">
            <Link to="/contact" data-cursor>
              <MagneticButton as="span" className="btn-cyan group rounded-full px-8 py-5 text-[15px] font-medium">
                Book a free strategy call
                <ArrowUpRight className="ml-1.5 h-4 w-4 transition-transform group-hover:rotate-45" />
              </MagneticButton>
            </Link>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}
